import styled, { keyframes } from 'styled-components';
import './App.css';
import SEO from './components/SEO';
import HubSpotForm from './components/HubSpotForm';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

// ─── Layout ─────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 40px;
  border-bottom: 1px solid #003f6b;
  position: sticky;
  top: 0;
  background: #002050;
  z-index: 100;

  @media (max-width: 600px) {
    padding: 14px 20px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #ffffff;

  em {
    font-style: normal;
    color: #5ba3f5;
  }
`;

const NavBadge = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #c8e4ff;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.18);
  padding: 4px 10px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #5ba3f5;
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

// ─── Hero ────────────────────────────────────────────────────────────────────

const HeroSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 100px 24px 80px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, var(--accent-light) 0%, var(--bg) 55%);

  @media (max-width: 600px) {
    padding: 60px 20px 56px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230078d4' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    pointer-events: none;
    opacity: 0.6;
  }
`;

const EyebrowLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  background: var(--accent-light);
  border: 1px solid rgba(0, 120, 212, 0.2);
  padding: 5px 14px;
  border-radius: 2px;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.6s ease both;
`;

const HeroHeadline = styled.h1`
  font-size: clamp(2.4rem, 6vw, 3.8rem);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  max-width: 760px;
  margin: 0 0 24px;
  animation: ${fadeUp} 0.6s ease 0.1s both;

  em {
    font-style: normal;
    color: var(--accent);
  }
`;

const HeroSub = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 560px;
  line-height: 1.7;
  margin: 0 0 48px;
  animation: ${fadeUp} 0.6s ease 0.2s both;
`;

const HeroCTA = styled.a`
  display: inline-block;
  padding: 12px 28px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 2px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;
  animation: ${fadeUp} 0.6s ease 0.3s both;

  &:hover {
    background: var(--accent-hover);
    color: #fff;
  }

  &:active {
    background: #005a9e;
  }
`;

const FormNote = styled.p`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin: 12px 0 0;
  animation: ${fadeUp} 0.6s ease 0.35s both;
`;

// ─── Early Access Section ─────────────────────────────────────────────────────

const EarlyAccessSection = styled.section`
  padding: 80px 24px;
  background: var(--surface);
  border-top: 1px solid var(--border);

  @media (max-width: 600px) {
    padding: 56px 20px;
  }
`;

const EarlyAccessInner = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const EarlyAccessHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

// ─── Pillars ─────────────────────────────────────────────────────────────────

const PillarsSection = styled.section`
  padding: 80px 24px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 600px) {
    padding: 56px 20px;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted);
  text-align: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  text-align: center;
  color: var(--text-primary);
  margin: 0 0 56px;
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-width: 480px;
    margin: 0 auto;
  }
`;

const PillarCard = styled.div`
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 32px 28px;
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
    border-color: var(--border-bright);
  }
`;

const PillarNumber = styled.div`
  font-family: var(--mono);
  font-size: 0.7rem;
  color: var(--accent);
  margin-bottom: 16px;
  letter-spacing: 0.06em;
`;

const PillarTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--text-primary);
  margin: 0 0 12px;
`;

const PillarBody = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 0 0 20px;
`;

const PillarTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: var(--accent);
  background: var(--accent-light);
  border: 1px solid rgba(0, 120, 212, 0.15);
  padding: 3px 9px;
  border-radius: 2px;
  font-family: var(--mono);
`;

// ─── Differentiators strip ────────────────────────────────────────────────────

const DiffSection = styled.section`
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  padding: 56px 24px;
`;

const DiffInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DiffItem = styled.div``;

const DiffLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

const DiffValue = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
`;

// ─── Comparison ───────────────────────────────────────────────────────────────

const CompareSection = styled.section`
  padding: 80px 24px;
  max-width: 860px;
  margin: 0 auto;
  width: 100%;
`;

const TableScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const CompareTable = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const CompareTh = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border);

  &:not(:first-child) {
    text-align: center;
  }
`;

const CompareTr = styled.tr`
  &:hover td {
    background: var(--accent-light);
  }
`;

const CompareTd = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text-secondary);
  vertical-align: middle;

  &:first-child {
    font-weight: 500;
    color: var(--text-primary);
  }

  &:not(:first-child) {
    text-align: center;
  }
`;

const Check = styled.span`
  color: #107c10;
  font-size: 1rem;
  font-weight: 600;
`;

const Cross = styled.span`
  color: var(--border-bright);
  font-size: 0.9rem;
`;

const Partial = styled.span`
  color: #a80000;
  font-size: 0.75rem;
  font-weight: 500;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

const FooterEl = styled.footer`
  border-top: 1px solid var(--border);
  background: var(--surface);
  padding: 36px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 28px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const FooterLeft = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);

  a {
    color: var(--text-secondary);
    &:hover { color: var(--accent); }
  }
`;

const FooterRight = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

// ─── Data ────────────────────────────────────────────────────────────────────

const pillars = [
  {
    num: '01',
    title: 'Process Capture From Anywhere',
    body: 'A DocType is a business process definition — fields, structure, workflow, rules. Once defined, it can receive data from internal forms, public-facing websites, automation pipelines, or direct API calls. Every observation becomes a first-class, searchable, governed record.',
    tags: ['Forms', 'Public API', 'n8n / webhooks', 'File upload', 'CRDT editing'],
  },
  {
    num: '02',
    title: 'Governance by Architecture',
    body: 'Documents in FusionDocs must exist inside designated projects and folders. The system does not let knowledge live outside the perimeter — not by policy, but because the architecture makes it impossible. When people leave, their knowledge stays.',
    tags: ['Enforced domicile', 'Immutable audit log', 'Tamper-evident', 'Hash-chained trail'],
  },
  {
    num: '03',
    title: 'AI-Native Smart Documents',
    body: 'Every document is a live, canonically addressed record. Share the address, not the file. AI agents, automations, and integrations consume FusionDocs records directly — structured, versioned, context-rich — without export steps or format conversion.',
    tags: ['Canonical address', 'Structured JSON', 'Agent-consumable', 'Versioned', 'Permissioned'],
  },
];

const diffs = [
  { label: 'Data portability', value: 'Your data is always exportable. No hostage situations.' },
  { label: 'No lock-in', value: 'Open stack. Switch or leave without losing anything.' },
  { label: 'AI integration', value: 'Native — live addressed records, no export step' },
  { label: 'Governance', value: 'Enforced by architecture, not by policy' },
];

const compareRows = [
  { feature: 'Document creation governance', gdrive: false, sharepoint: 'partial', fusion: true },
  { feature: 'Structured form capture (native)', gdrive: false, sharepoint: false, fusion: true },
  { feature: 'External data intake (API / forms)', gdrive: false, sharepoint: false, fusion: true },
  { feature: 'Hybrid search with ACL enforcement', gdrive: false, sharepoint: 'partial', fusion: true },
  { feature: 'Immutable, hash-chained audit trail', gdrive: false, sharepoint: false, fusion: true },
  { feature: 'Native e-signature (hashed + audited)', gdrive: false, sharepoint: false, fusion: true },
  { feature: 'AI-consumable without export', gdrive: false, sharepoint: false, fusion: true },
  { feature: 'Data portability (export any time)', gdrive: false, sharepoint: false, fusion: true },
  { feature: 'No vendor lock-in', gdrive: false, sharepoint: false, fusion: true },
];

function CellIcon({ value }) {
  if (value === true)      return <Check aria-label="yes">✓</Check>;
  if (value === false)     return <Cross aria-label="no">—</Cross>;
  if (value === 'partial') return <Partial aria-label="partial">Partial</Partial>;
  return null;
}

// ─── App ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Page>
      <SEO />

      {/* Nav */}
      <Nav>
        <Logo>
          <img src="/logo_on_black.png" alt="FusionDocs" height="32" style={{ display: 'block' }} /><span>Fusion<em>Docs</em></span>
        </Logo>
        <NavBadge>Early Access</NavBadge>
      </Nav>

      {/* Hero */}
      <HeroSection>
        <EyebrowLabel>Coming Soon — Governed Document Intelligence</EyebrowLabel>
        <HeroHeadline>
          Your knowledge leaves<br />when your people do.<br />
          <em>FusionDocs changes that.</em>
        </HeroHeadline>
        <HeroSub>
          Design a document to capture a business process. Capture data from anywhere.
          Keep it inside the perimeter — structured, searchable, and natively consumable
          by AI agents — without vendor lock-in.
        </HeroSub>
        <HeroCTA href="#early-access">Request Early Access</HeroCTA>
        <FormNote>No spam. Join the early access list.</FormNote>
      </HeroSection>

      {/* Differentiator strip */}
      <DiffSection aria-label="Key differentiators">
        <DiffInner>
          {diffs.map(d => (
            <DiffItem key={d.label}>
              <DiffLabel>{d.label}</DiffLabel>
              <DiffValue>{d.value}</DiffValue>
            </DiffItem>
          ))}
        </DiffInner>
      </DiffSection>

      {/* Three pillars */}
      <PillarsSection aria-labelledby="pillars-title">
        <SectionLabel>What makes it different</SectionLabel>
        <SectionTitle id="pillars-title">Built around three ideas that don't compromise</SectionTitle>
        <PillarsGrid>
          {pillars.map(p => (
            <PillarCard key={p.num}>
              <PillarNumber>{p.num}</PillarNumber>
              <PillarTitle>{p.title}</PillarTitle>
              <PillarBody>{p.body}</PillarBody>
              <PillarTags>
                {p.tags.map(t => <Tag key={t}>{t}</Tag>)}
              </PillarTags>
            </PillarCard>
          ))}
        </PillarsGrid>
      </PillarsSection>

      {/* Comparison table */}
      <CompareSection aria-labelledby="compare-title">
        <SectionLabel>How it compares</SectionLabel>
        <SectionTitle id="compare-title">
          Not another cloud drive.<br />Not another SaaS trap.
        </SectionTitle>
        <TableScroller>
          <CompareTable>
            <thead>
              <tr>
                <CompareTh scope="col">Capability</CompareTh>
                <CompareTh scope="col">Google&nbsp;/&nbsp;M365</CompareTh>
                <CompareTh scope="col">SharePoint&nbsp;/&nbsp;Confluence</CompareTh>
                <CompareTh scope="col" style={{ color: 'var(--accent)' }}>FusionDocs</CompareTh>
              </tr>
            </thead>
            <tbody>
              {compareRows.map(row => (
                <CompareTr key={row.feature}>
                  <CompareTd>{row.feature}</CompareTd>
                  <CompareTd><CellIcon value={row.gdrive} /></CompareTd>
                  <CompareTd><CellIcon value={row.sharepoint} /></CompareTd>
                  <CompareTd><CellIcon value={row.fusion} /></CompareTd>
                </CompareTr>
              ))}
            </tbody>
          </CompareTable>
        </TableScroller>
      </CompareSection>

      {/* Early Access Form */}
      <EarlyAccessSection id="early-access">
        <EarlyAccessInner>
          <EarlyAccessHeader>
            <SectionLabel>Early Access</SectionLabel>
            <SectionTitle style={{ margin: '0 0 12px' }}>Join the list</SectionTitle>
            <HeroSub style={{ margin: '0 auto', animation: 'none' }}>
              We're opening access to select teams. Tell us a bit about yourself
              and we'll be in touch.
            </HeroSub>
          </EarlyAccessHeader>
          <HubSpotForm />
        </EarlyAccessInner>
      </EarlyAccessSection>

      {/* Footer */}
      <FooterEl>
        <FooterLeft>
          © {new Date().getFullYear()} FusionDocs — built by{' '}
          <a href="https://saigonai.com" target="_blank" rel="noopener noreferrer">SaigonAI</a>.
          {' '}Questions?{' '}
          <a href="mailto:hello@fusiondocs.com">hello@fusiondocs.com</a>
        </FooterLeft>
        <FooterRight>
          Open stack · Your data is always portable · No lock-in
        </FooterRight>
      </FooterEl>
    </Page>
  );
}
